package com.evoapps.evoshift.domain.availability

import com.evoapps.evoshift.domain.model.AvailabilityDay
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/**
 * SH-165: filling in a month of availability at once.
 *
 * "As last month" maps by weekday, not by date: a person's week repeats (free on Mondays, never on
 * Saturday evenings), while the 14th falls on another weekday every month. Each day takes the same
 * weekday's occurrence in the previous month (the 2nd Monday from the 2nd Monday); a 5th occurrence
 * the previous month did not have takes its last one. Days the previous month left unanswered stay
 * as they are. The status and the hours are copied; comments are not (they are about one date:
 * "doctor at 10").
 */
object AvailabilityBulk {

    /** The month before "YYYY-MM", as "YYYY-MM". */
    fun previousMonth(month: String): String {
        val first = LocalDate.parse("$month-01").minus(DatePeriod(months = 1))
        return "${first.year}-${first.month.ordinal.plus(1).toString().padStart(2, '0')}"
    }

    /**
     * [current] (day of month to day) with [previous] (the month before) copied onto the [days] of
     * [month] ("YYYY-MM") by weekday as described above.
     */
    fun copyByWeekday(
        previous: Map<Int, AvailabilityDay>,
        current: Map<Int, AvailabilityDay>,
        month: String,
        days: Collection<Int>
    ): Map<Int, AvailabilityDay> {
        val prevFirst = LocalDate.parse("${previousMonth(month)}-01")
        val prevDates = generateSequence(prevFirst) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.month == prevFirst.month }.toList()
        val byWeekday = prevDates.groupBy { it.dayOfWeek }
        val first = LocalDate.parse("$month-01")
        val result = current.toMutableMap()
        for (day in days) {
            val date = runCatching { first.plus(DatePeriod(days = day - 1)) }.getOrNull()?.takeIf { it.month == first.month } ?: continue
            val sameWeekday = byWeekday[date.dayOfWeek].orEmpty()
            val source = sameWeekday.getOrNull((day - 1) / 7) ?: sameWeekday.lastOrNull() ?: continue
            val copied = previous[source.day] ?: continue
            val old = current[day]
            result[day] = AvailabilityDay(
                status = copied.status,
                ranges = copied.ranges,
                preferred = copied.preferred,
                comment = old?.comment,
                workplaceComments = old?.workplaceComments.orEmpty()
            )
        }
        return result
    }

    /** [current] with every one of [days] set to [status], hours and comments kept. */
    fun setStatus(current: Map<Int, AvailabilityDay>, days: Collection<Int>, status: AvailabilityStatus): Map<Int, AvailabilityDay> =
        current + days.associateWith { day -> (current[day] ?: AvailabilityDay(AvailabilityStatus.AVAILABLE)).copy(status = status) }
}
